import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import { Check, ArrowUpRight } from "lucide-react";
import { CATEGORIES } from "@/data/site";
import { CAT_DESC_AR } from "@/data/content_ar";
import { Reveal, Eyebrow } from "@/components/Reveal";
import { LogoMark } from "@/components/LogoMark";
import { useI18n } from "@/context/I18nContext";

export default function Products() {
  const { t, lang } = useI18n();
  const [active, setActive] = useState(CATEGORIES[0].id);
  const cat = CATEGORIES.find((c) => c.id === active);
  const desc = lang === "ar" ? CAT_DESC_AR[cat.id] || cat.desc : cat.desc;

  return (
    <section id="products" data-testid="products-section" className="relative z-10 mx-auto max-w-7xl px-6 lg:px-10 py-28 lg:py-40">
      <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
        <div>
          <Reveal><Eyebrow>{t("prod_eyebrow")}</Eyebrow></Reveal>
          <Reveal delay={0.05}>
            <h2 className="mt-5 font-display text-4xl sm:text-5xl lg:text-6xl tracking-tight text-brand-fg max-w-2xl leading-[1.02]">
              {t("prod_title1")} <span className="text-brand-gold italic">{t("prod_title2")}</span>
            </h2>
          </Reveal>
        </div>
        <Reveal delay={0.1}>
          <p className="max-w-sm text-brand-fg/70 text-sm leading-relaxed">{t("prod_intro")}</p>
        </Reveal>
      </div>

      <div className="mt-14 flex flex-wrap gap-3" data-testid="category-tabs">
        {CATEGORIES.map((c) => (
          <button
            key={c.id}
            data-testid={`category-tab-${c.id}`}
            onClick={() => setActive(c.id)}
            className={`rounded-full px-5 py-2.5 text-sm transition-colors border ${
              active === c.id
                ? "bg-brand-gold border-brand-gold text-white font-semibold"
                : "border-brand-border bg-brand-card text-brand-fg/70 hover:border-brand-gold/60 hover:text-brand-gold"
            }`}
          >
            {c.name}
          </button>
        ))}
      </div>

      <AnimatePresence mode="wait">
        <motion.div
          key={cat.id}
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -16 }}
          transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
          className="mt-10 grid lg:grid-cols-12 gap-6"
        >
          <div className="lg:col-span-5 group relative overflow-hidden rounded-2xl border border-brand-border min-h-[360px] shadow-sm">
            <img src={cat.image} alt={cat.name} className="absolute inset-0 h-full w-full object-cover transition-transform duration-[1.2s] ease-out group-hover:scale-110" />
            {cat.image2 && (
              <div className="absolute bottom-28 right-5 z-10 w-28 h-28 rounded-xl overflow-hidden border-2 border-white/80 shadow-lg">
                <img src={cat.image2} alt="Desi Ghee" className="h-full w-full object-cover" />
                <span className="absolute bottom-0 inset-x-0 bg-black/60 text-white text-[10px] font-medium text-center py-0.5">Desi Ghee</span>
              </div>
            )}
            <div className="absolute inset-0 bg-gradient-to-t from-brand-ink/85 via-brand-ink/25 to-transparent" />
            <LogoMark className="absolute top-5 left-5 z-10" />
            <div className="absolute top-5 right-5 font-display text-5xl text-white/90">{cat.caption}</div>
            <div className="absolute bottom-6 left-6 right-6">
              <h3 className="font-display text-3xl text-white">{cat.name}</h3>
              <p className="mt-2 text-sm text-white/80">{desc}</p>
            </div>
          </div>

          <div className="lg:col-span-7 rounded-2xl border border-brand-border bg-brand-card p-8 sm:p-10 shadow-sm">
            <div className="grid sm:grid-cols-2 gap-x-8 gap-y-1">
              {cat.items.map((item, i) => (
                <motion.div
                  key={item}
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: i * 0.04 }}
                  data-testid={`product-item-${cat.id}-${i}`}
                  className="flex items-center gap-3 border-b border-brand-border py-3.5 group/item"
                >
                  <span className="grid place-items-center h-6 w-6 rounded-full bg-brand-goldSoft text-brand-gold shrink-0 group-hover/item:bg-brand-gold group-hover/item:text-white transition-colors">
                    <Check size={13} />
                  </span>
                  <span className="text-brand-fg/85 text-sm">{item}</span>
                </motion.div>
              ))}
            </div>
            <div className="mt-8 flex flex-wrap items-center gap-3">
              <a href="#inquiry" data-testid={`category-quote-${cat.id}`} className="inline-flex rounded-full border border-brand-gold/60 px-6 py-2.5 text-sm text-brand-gold hover:bg-brand-gold hover:text-white transition-colors">
                {t("prod_request")} {cat.name}
              </a>
              <Link to={`/product/${cat.id}`} data-testid={`category-details-${cat.id}`} className="inline-flex items-center gap-1 rounded-full bg-brand-fg/5 px-5 py-2.5 text-sm text-brand-fg hover:text-brand-gold transition-colors">
                {lang === "ar" ? "التفاصيل" : "View details"} <ArrowUpRight size={15} />
              </Link>
            </div>
          </div>
        </motion.div>
      </AnimatePresence>
    </section>
  );
}
